
export { DataTable, InfiniteScrollerMode } from "./dataTable";

export { Sort } from "./sort";

export class SortState<TItem, TParams = void> {
  public constructor(public column: IColumnDefinition<TItem, TParams>, public readonly sortIndex: number) {
  }
}

export interface ITableDefinition<TItem, TParams = {}> {
  title: string;
  buildDescription?: (items: TItem[]) => string;
  tableActions?: ITableAction[];
  rowActionSet?: IRowActionSet<TItem>;
  columns: IColumnDefinition<TItem, TParams>[];
  filters?: IFilterDefinition<TItem, TParams>[];
  defaultFilters?: TParams;
  getItemId: (item: TItem) => number;
  defaultSort: SortState<TItem, TParams>;
  rowClassName?: (item: TItem) => string;
  csv?: ICsvDefinition<TItem>;
  disableQueryString?: boolean;
}

export interface ITableAction {
  label: string;
  icomoon: string;
  action: () => void;
  isVisible?: () => boolean;
}

export type RowAction<T> = IRowAction<T>|IRowActionSet<T>;

export interface IRowActionSet<TItem> {
  isGroup: true;
  icomoon?: string;
  label: string|((item: TItem) => string);
  visible?: (item: TItem) => boolean;
  actions: RowAction<TItem>[];
}

export interface IRowAction<TItem> {
  isGroup: false;

  icomoon?: string;
  label: string|((item: TItem) => string);
  visible?: (item: TItem) => boolean;
  actionSingle: (item: TItem) => Promise<any>|any;
  render?: (item: TItem, action: () => Promise<any>|any) => JSX.Element;
}

export * from "./rowActionList";

export interface ICsvDefinition<TItem> {
  columns: {
    [name: string]: (item: TItem) => string;
  };
}

export const COL_DEFAULT_MIN_WIDTH = 100;

export interface IColumnDefinition<TItem, TParams = {}> {
  header?: string;
  description?: string;
  buildCell: (item: TItem) => JSX.Element | string;
  colClassName?: string;
  sorts?: ISortDefinition<TItem, TParams>[];
  colStyle?: (item: TItem) => any;
  isVisible?: () => boolean;

  /**
   * Value of flex-grow property of each cell. Default: COL_DEFAULT_FLEX_GROW
   */
  fixedWidth?: boolean;

  /**
   * Minimum width in pixels. Default: COL_DEFAULT_MIN_WIDTH
   */
  minWidth?: number;

  /**
   * If set, this column might not be shown with less available width.
   * A higher value means the column will hide before others if it needs to.
   */
  shyness?: number | null;
}

export interface ISortDefinition<TItem, TParams> {
  description: string;
  icomoon?: string;

  /**
   * Server-side sorting. If defined, these are parameters to be sent with any request to the API.
   */
  parameters?: TParams;

  /**
   * Client-side sorting. If defined, this function should compare the two items,
   * and return -1 if a should be before b, and 1 if b should be before a.
   * This will be used with JS' Array.sort() method, after a page of items is retrieved from the API.
   */
  compare?: (a: TItem, b: TItem) => number;
}

export interface IFilterDefinition<TItem, TFilter extends {}> {
  /**
   * Used in key fragment and classname. Must be unique within the table definition it's applied to
   */
  keyFragment: string;

  /**
   * Describe applicable bit of the filter, in friendly terms. E.g. Since one month ago
   */
  describe: (current: TFilter) => string;

  /**
   * Default value to render, if there isn't one.
   */
  default: TFilter;

  /**
   * Width in pixels of this filter - used to determine which filters to display
   * Assumed by dataTable.tsx to be 300 if not present
   */
  width?: number;

  /**
   * The primitive types of the default values, i.e.
   *
   * types: {
   *   statuses: "array",
   *   endTime: "datetime",
   * }
   *
   * Used when parsing the value to create an array
   * of strings/numbers, or parsing a datetime
   */
  types?: { [key: string]: string };

  /**
   * UI to render to change the value of this filter. Include a label if desired
   */
  render: (filters: TFilter, onChange: (newFilters: TFilter) => void) => JSX.Element;

  /**
   * Whether this filter should be usable.
   */
  enabled?: () => boolean;
}
